import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import CookieJs from "js-cookie";
import { get } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import Breadcrumb from "../components/Breadcrumb";
import MainLayout from "../components/MainLayout";
import ProtectedPermissionPage from "../components/ProtectedPermissionPage";
import useNotification from "../hooks/useNotification";
import categoryService from "../services/category";
import eventService from "../services/event";
import role from "../services/role";
import userService from "../services/users";
import queryDefault from "../utils/queryDefault";

export default function UserEdit() {
  const notification = useNotification();
  const { id } = useParams();
  const navigate = useNavigate();
  const user = JSON.parse(CookieJs.get("USER") || "");
  const [showEvent, setShowEvent] = useState(true);
  const schema = yup.object().shape({
    name: yup
      .string()
      .min(5)
      .required("name is a required field and length > 5"),
    event_id: yup.string().required("event is a required field"),
    role_id: yup.string().required("role is a required field"),
    username: yup
      .string()
      .min(5)
      .matches(/^[a-z0-9]+$/, "lowercase, no symbol and space")
      .required("username is a required field and length > 5"),
  });
  const form = useForm({
    defaultValues: {
      id: "",
      name: "",
      username: "",
      phone_number: "",
      event_id: "",
      role_id: "",
      admin_name: user.username,
      user_category: []
    },
    resolver: yupResolver(schema),
  });
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const subscription = form.watch((value) => {
      if (Number(value.role_id) === 7) {
        setShowEvent(false);
        if (value.event_id !== "") {
          form.setValue("event_id", "");
        }
      } else {
        setShowEvent(true);
      }
    });
    return () => subscription.unsubscribe?.();
  }, [form]);

  const updateUser = useMutation("create_user", userService.editUser, {
    onSuccess: () => {
      navigate("/app/users/");

      notification.onOpen({
        message: "User was successfully updated!",
        type: "success",
        position: "top",
      });
    },
    onError: () => {
      notification.onOpen({
        message: "Failed Update User",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const onSubmit = (values: any) => {
    values.user_category = categories.filter((category: any) => category.checked).map((category: any) => category.id);
    updateUser.mutate({
      data: values,
    });
  };

  const listEvent = useQuery("list-event", () =>
    eventService.getAllEvent({ keyword: "" })
  );

  const listRole = useQuery("list-role", () =>
    role.getAllRole({})
  );

  const fetchCategories = useCallback(() => {
    if (!form.getValues("event_id")) return;
    categoryService.listEventCategories(Number(form.getValues("event_id")))
      .then(({ data }) => {
        const transformedCategories = data.map((category: any) => ({
          id: category.id,
          name: category.category,
          checked: checkIsCategoryChecked(category.id, form.getValues("user_category"))
        }));
        setCategories(transformedCategories);
      })
  }, [form]);

  useEffect(() => {
    if (id) {
      userService.viewUser(id)
        .then(({ data }) => {
          form.setValue("id", id);
          form.setValue("name", data.name);
          form.setValue("user_category", data.user_category || []);
          form.setValue("event_id", data.event_id);
          form.setValue("phone_number", data.phone_number);
          form.setValue("role_id", data.role_id);
          form.setValue("username", data.username);
          fetchCategories();
        })
    }
  }, [id, form, fetchCategories]);

  const checkIsCategoryChecked = (categoryId: number, userCategories: any[]) => {
    return userCategories.some((userCategory: any) => userCategory.id === categoryId);
  };

  return (
    <MainLayout>
      <Breadcrumb
        label="Edit User"
        breadcrumbs={[{ label: "Users", href: "/app/users" }]}
      />

      <ProtectedPermissionPage acceptPermissions={[7, 99]}>
        <Box sx={{ mt: 2 }}>
          <Grid container>
            <Grid item lg={4} xs={12}>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Username</FormLabel>
                <Controller
                  name="username"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Name</FormLabel>
                <Controller
                  name="name"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Phone Number</FormLabel>
                <Controller
                  name="phone_number"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>
              {showEvent && (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Event</FormLabel>
                  <Controller
                    name="event_id"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <Box>
                        <Select
                          {...field}
                          size="small"
                          error={Boolean(fieldState.error?.message)}
                          fullWidth
                          onChange={event => {
                            form.setValue("event_id", event.target.value);
                            fetchCategories();
                          }}
                        >
                          {get(
                            listEvent,
                            ["data", "data"],
                            []
                          ).map((item: any, i: any) => (
                            <MenuItem value={item?.id} key={item?.id}>
                              {item?.name}
                            </MenuItem>
                          ))}
                        </Select>

                        <FormHelperText error={Boolean(fieldState.error?.message)}>
                          {fieldState.error?.message}
                        </FormHelperText>
                      </Box>
                    )}
                  />
                </FormControl>
              )}
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Role</FormLabel>
                <Controller
                  name="role_id"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <Box>
                      <Select
                        {...field}
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        fullWidth
                      >
                        {get(
                          listRole,
                          ["data", "data"],
                          []
                        ).map((item: any, i: any) => (
                          <MenuItem value={item?.id} key={item?.id}>
                            {item?.name}
                          </MenuItem>
                        ))}
                      </Select>

                      <FormHelperText error={Boolean(fieldState.error?.message)}>
                        {fieldState.error?.message}
                      </FormHelperText>
                    </Box>
                  )}
                />
              </FormControl>
              {showEvent && (
                <Box sx={{ mt: 2 }}>
                  <FormLabel>Categories</FormLabel>
                  {categories.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, mb: 1, borderBottom: '1px solid #e0e0e0', pb: 1 }}>
                      <input
                        type="checkbox"
                        checked={categories.every((category: any) => category.checked)}
                        onChange={(e) => {
                          const updatedCategories: any = categories.map((cat: any) => ({
                            ...cat,
                            checked: e.target.checked
                          }));
                          setCategories(updatedCategories);
                        }}
                      />
                      <span style={{ marginLeft: 8, fontWeight: 'bold' }}>Select All</span>
                    </Box>
                  )}
                  {categories.map((category: any) => (
                    <Box key={category.id} sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <input
                        type="checkbox"
                        checked={category.checked}
                        onChange={(e) => {
                          const updatedCategories: any = categories.map((cat: any) =>
                            cat.id === category.id ? { ...cat, checked: e.target.checked } : cat
                          );
                          setCategories(updatedCategories);
                        }}
                      />
                      <span style={{ marginLeft: 8 }}>{category.name}</span>
                    </Box>
                  ))}
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={1}>
            <LoadingButton
              variant="contained"
              onClick={form.handleSubmit(onSubmit)}
              loading={updateUser.isLoading}
            >
              SAVE
            </LoadingButton>

            <Button variant="outlined" onClick={() => navigate(-1)}>
              CANCEL
            </Button>
          </Stack>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
